import * as React from 'react';

import { ApplicationState, ConnectedReduxProps } from './store';
import { Button, Modal, ThemeProvider, createTheme } from '@mui/material';
import { Dispatch, Store } from 'redux';
import { Provider, connect } from 'react-redux';
import { Navigate, Route, Routes, BrowserRouter  } from 'react-router';
import { Slide, ToastContainer } from 'react-toastify';
import { confirmationModalCancel, confirmationModalOK, setCurrentUserInfo } from './store/shared/actions';

import ChangePassword from './pages/ChangePassword/ChangePassword';
import { ConfirmationModal } from './store/shared/model';
import Details from './pages/Details/Details';
import ErrorBoundary from './components/custom/errorBoundary';
import Home from './pages/Home';
import Login from './pages/Login/Login';
import LoginOKTA from './pages/Login/LoginOKTA';
import MfaAuthenticate from './pages/Login/MfaAuthenticate';
import { LoginStorageModel } from './store/login/model';
import MobileImages from './pages/Images/MobileImages';
import PrivateRoute  from './components/custom/private-route';
import checkInternetConnection from './store/utils/api/checkInternetConnection';
import { getStorage } from './store/utils/storage/storage.manager';
import { networkStatusChanged } from './store/network/actions';
import styles from './main.module.css';
import NotFound from './components/custom/not-found';
import NavigationListener from './navigationListener';

interface PropsFromDispatch {
  readonly [key: string]: any;
  readonly networkStatusChanged: typeof networkStatusChanged;
  readonly setCurrentUserInfo: typeof setCurrentUserInfo;
  readonly confirmationModalOK: typeof confirmationModalOK;
  readonly confirmationModalCancel: typeof confirmationModalCancel;
}

interface PropsFromState {
  readonly isUIDisabled: boolean;
  readonly confirmationModal?: ConfirmationModal;
}

// Any additional component props go here.
interface OwnProps {
  readonly store: Store<ApplicationState>;
}

// Create an intersection type of the component props and our Redux props.
type AllProps = PropsFromDispatch & PropsFromState & OwnProps & ConnectedReduxProps;

class Main extends React.Component<AllProps> {
  async componentDidMount() {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    const storageProvider = getStorage();
    const storageInstance = storageProvider.getInstance(LoginStorageModel);
    const userInfo = await storageInstance.find((item: LoginStorageModel) => {
      if (item && item.modelGuid === 'token') {
        return true;
      }
      return false;
    });
    if (userInfo) {
      this.props.setCurrentUserInfo(userInfo.languageCode, userInfo.username, userInfo.firstName, userInfo.lastName, userInfo.parentPartyName, userInfo.parentPartyDibCode, userInfo.parentPartyId, userInfo.useMfa, userInfo.roles, userInfo.mfaQRCode, userInfo.registeredForMfa);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
  }

  handleOnline() {
    checkInternetConnection()
      .then(() => {
        this.props.networkStatusChanged(true);
      })
      .catch(() => {
        this.props.networkStatusChanged(false);
      });
  }

  handleOffline() {
    this.props.networkStatusChanged(false);
  }

  public render() {
    const { store } = this.props;
    const belfiusTheme = createTheme({
      typography: {
        // Use the system font instead of the default Roboto font.
        fontFamily: [
          'RobotoMono',
        ].join(','),
        fontSize: 14
      },
      components: {
        MuiIconButton: {
          styleOverrides: {
            root: {
              padding: '0 6px',
            },
          },
        },
        MuiFormGroup: {
          styleOverrides: {
            root: {
              flexWrap: 'unset',
              flexDirection: 'row',
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              marginLeft: '0',
              marginRight: '0',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              margin: '0.5rem',
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              padding: '0 5px',
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              '&.Mui-checked': {
                color: '#f50057',
              },
            },    
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '&.Mui-checked': {
                color: '#f50057',
              },
            },    
          },
        },
      }
    });

    const showConfirmationOkButton = this.props.confirmationModal && this.props.confirmationModal.okText !== null;
    const confirmationOkButtonText = this.props.confirmationModal && this.props.confirmationModal.okText !== '' ? this.props.confirmationModal.okText : 'Ok';

    const showConfirmationCancelButton = this.props.confirmationModal && this.props.confirmationModal.cancelText !== null;
    const confirmationCancelButtonText = this.props.confirmationModal && this.props.confirmationModal.cancelText !== '' ? this.props.confirmationModal.cancelText : 'Cancel';

    const confirmationModalOkButton =
      <Button variant='contained' color='primary' className={styles.button} onClick={() => this.props.confirmationModalOK()}>
        {confirmationOkButtonText}
      </Button>;

    const confirmationModalCancelButton =
      <Button variant='contained' color='secondary' className={styles.button} onClick={() => this.props.confirmationModalCancel()}>
        {confirmationCancelButtonText}
      </Button>;


    const confirmationModal = (
      <Modal
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        open={this.props.confirmationModal !== undefined}
      >
        <div className={styles.modal}>
          <ul>
            {this.props.confirmationModal ? this.props.confirmationModal.messages.map((message) => {
              return <li key={message}>{message}</li>;
            }) : <li></li>}
          </ul>
          <div className={styles.modalButtons}>
            {showConfirmationOkButton && confirmationModalOkButton}
            {showConfirmationCancelButton && confirmationModalCancelButton}
          </div>
        </div>
      </Modal>
    );

    return (
      <ThemeProvider theme={belfiusTheme}>
        <Provider store={store}>
        <ErrorBoundary>
          <BrowserRouter>
          <NavigationListener />
            <div className={styles.enabled}>
              <div hidden={!this.props.isUIDisabled} className={styles.overlay}>
                <div className={styles.loader}><img alt="" src={require('./assets/images/spinnerLoaderGray.gif')} /></div>
              </div>
              <div className={this.props.isUIDisabled ? styles.disabled : styles.enabled}>
                <ToastContainer
                  position='top-right'
                  autoClose={5000}
                  hideProgressBar={false}
                  transition={Slide}
                  newestOnTop
                  closeOnClick
                  rtl={false}
                  pauseOnVisibilityChange
                  draggable
                  pauseOnHover
                />
                <Routes>
                  <Route path='/login' element={<Login {...this.props} />} />
                  <Route path='/implicit/callback' element={<LoginOKTA {...this.props} />} />
                  <Route path='/mfaAuthenticate' element={<MfaAuthenticate {...this.props} />} />
                  <Route path='/' element={<PrivateRoute Component={Home} dispatch={this.props.dispatch} />} />
                  <Route path='/changePassword' element={<PrivateRoute Component={ChangePassword} dispatch={this.props.dispatch} />} />
                  <Route path='/images/:instructionId' element={<PrivateRoute Component={MobileImages} dispatch={this.props.dispatch} />} />
                  <Route path='/details/:instructionId' element={<PrivateRoute Component={Details} dispatch={this.props.dispatch} />} />
                  <Route path='/not-found' element={<PrivateRoute Component={NotFound} />} />
                  <Route path='*' element={<Navigate to="/" replace />} />
                </Routes>
                {confirmationModal}
              </div>
            </div>
          </BrowserRouter>
          </ErrorBoundary>
        </Provider>
      </ThemeProvider>
    );
  }
}

// It's usually good practice to only include one context at a time in a connected component.
// Although if necessary, you can always include multiple contexts. Just make sure to
// separate them from each other to prevent prop conflicts.
const mapStateToProps = ({ shared }: ApplicationState): PropsFromState => ({
  isUIDisabled: shared.isUIDisabled,
  confirmationModal: shared.confirmationModal
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setCurrentUserInfo: (currentLanguage: string, username: string, firstName: string, lastName: string, parentPartyName: string, parentPartyDibCode: string, parentPartyId: number, useMfa: boolean, roles: string[], mfaQRCode: string, registeredMfa: boolean) =>
  dispatch(setCurrentUserInfo(currentLanguage, username, firstName, lastName, parentPartyName, parentPartyDibCode, parentPartyId, useMfa, roles, mfaQRCode, registeredMfa)),
  networkStatusChanged: (status: boolean) => dispatch(networkStatusChanged(status)),
  confirmationModalOK: () => dispatch(confirmationModalOK()),
  confirmationModalCancel: () => dispatch(confirmationModalCancel())
});

// Normally you wouldn't need any generics here (since types infer from the passed functions).
// But since we pass some props from the `index.js` file, we have to include them.
// For an example of a `connect` function without generics, see `./containers/LayoutContainer`.
export default connect(mapStateToProps, mapDispatchToProps)(Main);
